/**
 * GONGFU - Main App Component
 * Orchestrates the main application layout and routing
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import MainLayout from './components/Layout/MainLayout';
import Onboarding from './components/Onboarding/Onboarding';
import Dashboard from './components/Dashboard/Dashboard';
import ScenarioView from './components/Scenario/ScenarioView';
import Login from './components/Auth/Login';
import Landing from './components/Landing/Landing';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProgressProvider } from './contexts/ProgressContext';
import { getFirstScenario } from './utils/scenarioSequencing';
import { markOnboardingComplete } from './utils/api';
import { supabase } from './lib/supabase';

function AppContent() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      const localOnboarding = localStorage.getItem('pm-sim-onboarding-complete') === 'true';
      
      if (isAuthenticated && user && supabase) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;
          
          if (token) {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/user/onboarding-status`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              setHasCompletedOnboarding(data.hasCompletedOnboarding || localOnboarding);
            } else {
              setHasCompletedOnboarding(localOnboarding);
            }
          } else {
            setHasCompletedOnboarding(localOnboarding);
          }
        } catch (error) {
          console.error('Error checking onboarding status:', error);
          setHasCompletedOnboarding(localOnboarding);
        }
      } else {
        setHasCompletedOnboarding(localOnboarding);
      }
      
      setLoading(false);
    };

    checkOnboardingStatus();
  }, [isAuthenticated, user]);

  const handleOnboardingComplete = async () => {
    localStorage.setItem('pm-sim-onboarding-complete', 'true');
    setHasCompletedOnboarding(true);
    
    if (user) {
      try {
        await markOnboardingComplete();
      } catch (error) {
        console.error('Error saving onboarding status:', error);
      }
    }
  };

  if (authLoading || loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: 'var(--text-secondary)'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        {/* Public routes - landing first */}
        <Route path="/" element={<Landing />} />
        <Route path="/landing" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        
        {/* Protected routes - app under /app */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              {hasCompletedOnboarding ? (
                <Navigate to={`/scenario/${getFirstScenario()}`} replace />
              ) : (
                <Onboarding onComplete={handleOnboardingComplete} />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              {!hasCompletedOnboarding ? (
                <Navigate to="/onboarding" replace />
              ) : (
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/scenario/:scenarioId"
          element={
            <ProtectedRoute>
              {!hasCompletedOnboarding ? (
                <Navigate to="/onboarding" replace />
              ) : (
                <MainLayout>
                  <ScenarioView />
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <Navigate to="/app" replace />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ProgressProvider>
          <Router>
            <AppContent />
          </Router>
        </ProgressProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;


